import { bunja, createScope } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { createContext } from 'react';

import { getPopupApi, putPopupMarkAsReadApi } from '@flipster/apis/v1/popup';
import { Popup, PopupId, PopupPage } from '@flipster/apis/v1/popup/types';
import { overlayItemBunja } from '@flipster/overlay';
import { alert } from '@flipster/utils/alert';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { authBunja } from '../../shared/auth/state';
import { abTestBunja } from '../ab-test';
import { followOnboardingBunja } from './modal/follow/bunja';

export const EntryPageScope = createScope<PopupPage>();
const getPopupApiQueryKey = 'getPopupApi';

export const entryPopupBlockBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const blockedPopupAtom = atom<PopupId[]>([]);
  function block(id: PopupId) {
    store.set(blockedPopupAtom, [...new Set([...store.get(blockedPopupAtom), id])]);
  }
  function unblock(id: PopupId) {
    store.set(
      blockedPopupAtom,
      store.get(blockedPopupAtom).filter((i) => i != id),
    );
  }
  function peek(id: PopupId) {
    return store.get(blockedPopupAtom).includes(id);
  }
  function peekAndUnblock(id: PopupId) {
    if (peek(id)) return unblock(id), true;
    return false;
  }
  return { block, unblock, peek, peekAndUnblock };
});

export const entryPopupBunja = bunja(() => {
  const entry = bunja.use(EntryPageScope);
  const { getVariantAtom } = bunja.use(abTestBunja);
  const { peekAndUnblock } = bunja.use(entryPopupBlockBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const { showFollowOnboardingAtom } = bunja.use(followOnboardingBunja);
  const { variantAtom } = getVariantAtom('25q3-social-recurring-tiered-tc-display');
  const popupBaseAtom = atomWithQuery((get) => ({
    queryKey: [getPopupApiQueryKey, entry],
    queryFn: () => getPopupApi(),
    enabled: get(isCookieLoggedInAtom),
  }));
  const popupAtom = atom((get): Popup | undefined => {
    const popups = get(popupBaseAtom).data?.data.popups ?? [];
    // follow onboarding will be included later
    if (get(showFollowOnboardingAtom))
      popups.push({ id: 'ADHOC_FOLLOW_ONBOARDING', pages: ['WEB_MAIN', 'WEB_ASSETS'], description: '', data: {} });
    const top: Popup | undefined = popups
      .filter((popup) => {
        if (popup.id === 'RECURRING_TC_OPEN') return get(variantAtom) === 'treatment';
        return true;
      })
      .filter((popup) => popup.pages.includes(entry))[0];
    if (top && !peekAndUnblock(top.id)) return top;
  });
  popupAtom.debugLabel = 'popupAtom';
  return { showFollowOnboardingAtom, popupAtom };
});

export const PopupContext = createContext<Popup>(null!);
const PopupScope = createScopeFromContext(PopupContext);

export const entryPopupItemBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const popup = bunja.use(PopupScope);
  const { close } = bunja.use(overlayItemBunja);
  async function markAsRead(id: PopupId) {
    const queryClient = store.get(queryClientAtom);
    try {
      await putPopupMarkAsReadApi(id);
      // ignore alreadyMarkedAsRead
    } catch (error) {
      alert.error(error);
    } finally {
      await queryClient.invalidateQueries({ queryKey: [getPopupApiQueryKey] });
    }
  }
  function onClose() {
    markAsRead(popup.id);
    close();
  }
  return { markAsRead, onClose, popup };
});
